import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { Button, Badge, Alert, Modal } from 'react-bootstrap';
import { FaMapMarkerAlt, FaCheckCircle, FaClock, FaCog, FaMobile, FaRedo } from 'react-icons/fa';
import { toast } from 'react-toastify';
import API from '../../services/api';
import SoftwareRatingModal from '../SoftwareRatingModal';
import './CompactAttendanceBar.css';

const CompactAttendanceBar = () => {
 const { user } = useContext(AuthContext);
 const navigate = useNavigate();
 const [status, setStatus] = useState('loading');
 const [checkInTime, setCheckInTime] = useState(null);
 const [error, setError] = useState('');
 const [now, setNow] = useState(new Date());
 const [isDesktop, setIsDesktop] = useState(false);
 const [showModal, setShowModal] = useState(false);
 const [modalAction, setModalAction] = useState('');
 const [hasShownModal, setHasShownModal] = useState(false);
 const [showDeviceModal, setShowDeviceModal] = useState(false);
 const [showLocationModal, setShowLocationModal] = useState(false);
 const [locationErrorData, setLocationErrorData] = useState(null);
 const [showRatingModal, setShowRatingModal] = useState(false);
 const [hasRated, setHasRated] = useState(null); // null = chưa check, true/false = đã check

 // Phát hiện thiết bị
 useEffect(() => {
 const userAgent = typeof window.navigator === 'undefined' ? '' : navigator.userAgent;
 const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
 setIsDesktop(!mobileRegex.test(userAgent));
 }, []);

 // Update time
 useEffect(() => {
 const timer = setInterval(() => setNow(new Date()), 60000);
 return () => clearInterval(timer);
 }, []);

 // Check if user has rated software
 useEffect(() => {
 const checkRating = async () => {
 if (!user) return;
 try {
 const { data } = await API.get('/software-rating/check');
 console.log('Rating check result:', data);
 setHasRated(data.hasRated);
 } catch (err) {
 console.error('Error checking rating:', err);
 // Nếu lỗi API, cho phép check-in (không block user)
 setHasRated(true);
 }
 };
 checkRating();
 }, [user]);

 // Fetch status
 useEffect(() => {
 const fetchStatus = async () => {
 if (!user) return;
 try {
 setStatus('loading');
 const { data } = await API.get('/attendance/status/today');
 setStatus(data.status || 'not_checked_in');
 if (data.checkInTime) {
 setCheckInTime(new Date(data.checkInTime));
 }
 } catch (err) {
 if (err.response && err.response.status !== 404) {
 setError('Không thể tải trạng thái chấm công');
 setStatus('error');
 } else {
 setStatus('not_checked_in');
 }
 }
 };
 fetchStatus();
 }, [user]);

 // Auto show modal on mobile
 useEffect(() => {
 if (!user || isDesktop || hasShownModal || status === 'loading' || hasRated === null) return;

 const hour = now.getHours();
 const dayOfWeek = now.getDay();
 let isCheckinTime, isCheckoutTime;

 if (dayOfWeek === 6) { // Saturday
 isCheckinTime = hour >= 7 && hour < 11;
 isCheckoutTime = hour >= 11 && hour < 15;
 } else if (dayOfWeek >= 1 && dayOfWeek <= 5) { // Monday-Friday
 isCheckinTime = hour >= 5 && hour < 16;
 isCheckoutTime = hour >= 16 && hour < 22;
 }

 // Auto show modal for check-in
 if (status === 'not_checked_in' && isCheckinTime) {
 setTimeout(() => {
 console.log('Auto-show check-in modal, hasRated:', hasRated);

 // Nếu chưa đánh giá, hiện modal rating trước
 if (hasRated === false) {
 console.log('User chưa đánh giá, hiện modal rating');
 setShowRatingModal(true);
 setHasShownModal(true);
 } else {
 // Đã đánh giá rồi, hiện modal check-in
 console.log('User đã đánh giá, hiện modal check-in');
 setModalAction('check-in');
 setShowModal(true);
 setHasShownModal(true);
 }
 }, 500); // Delay 500ms for smooth UX
 }
 // Auto show modal for check-out
 else if (status === 'checked_in' && isCheckoutTime) {
 setTimeout(() => {
 setModalAction('check-out');
 setShowModal(true);
 setHasShownModal(true);
 }, 500);
 }
 }, [user, isDesktop, status, hasShownModal, now, hasRated]);

 const openModal = (action) => {
 console.log('openModal called:', { action, hasRated });

 // Kiểm tra rating trước khi cho check-in
 if (action === 'check-in' && hasRated === false) {
 console.log('User chưa đánh giá, hiện modal rating');
 setShowRatingModal(true);
 return;
 }

 console.log('Cho phép check-in, hasRated:', hasRated);
 setModalAction(action);
 setShowModal(true);
 setError('');
 };

 const handleRatingSubmitted = () => {
 setHasRated(true);
 setShowRatingModal(false);
 // Sau khi đánh giá xong, tự động mở modal check-in
 setTimeout(() => {
 setModalAction('check-in');
 setShowModal(true);
 }, 300);
 };

 const closeModal = () => {
 setShowModal(false);
 setModalAction('');
 setError('');
 };

 const performCheck = async (action) => {
 if (!navigator.geolocation) {
 setError('Trình duyệt không hỗ trợ GPS');
 return;
 }

 setStatus('loading');
 setError('');
 closeModal();

 try {
 // Device verification
 await API.post('/users2/devices/attendance/verify');

 // Get location
 const position = await new Promise((resolve, reject) => {
 navigator.geolocation.getCurrentPosition(resolve, reject, {
 enableHighAccuracy: true,
 timeout: 15000,
 maximumAge: 0
 });
 });

 const coordinates = {
 lat: position.coords.latitude,
 lon: position.coords.longitude,
 };

 if (action === 'check-in') {
 const { data } = await API.post('/attendance/check-in', {
 coordinates,
 accuracy: position.coords.accuracy,
 isDesktop
 });
 setStatus('checked_in');
 setCheckInTime(new Date(data.attendance.checkInTime));
 setHasShownModal(false); // Reset để modal có thể hiện lại khi check-out
 toast.success('Check-in thành công!');
 } else if (action === 'check-out') {
 const { data } = await API.post('/attendance/check-out', {
 coordinates,
 accuracy: position.coords.accuracy,
 isDesktop
 });
 setStatus('checked_out');
 toast.success('Check-out thành công!');
 }
 } catch (err) {
 console.error('Attendance error:', err);

 // Enhanced error handling with detailed logging
 const apiError = err.response?.data?.message || `Lỗi khi ${action}`;
 const errorCode = err.response?.data?.errorCode;
 const statusCode = err.response?.status;

 console.log('[COMPACT-ERROR] Error details:', {
 statusCode,
 errorCode,
 apiError,
 fullResponse: err.response?.data
 });

 // Check for DEVICE_NOT_REGISTERED first (priority check)
 if (errorCode === 'DEVICE_NOT_REGISTERED') {
 console.log('[COMPACT-ERROR] Device not registered - showing modal');
 setShowDeviceModal(true);
 setStatus('not_checked_in');
 return;
 }

 // Check for LOCATION_VALIDATION_FAILED (priority check)
 if (errorCode === 'LOCATION_VALIDATION_FAILED') {
 console.log('[COMPACT-ERROR] Location validation failed - showing modal');
 // Lấy thông tin chi tiết về khoảng cách từ response
 const validationDetails = err.response?.data?.validationDetails;
 const allowedLocations = err.response?.data?.allowedLocations;
 const nearestDistance = validationDetails?.distances?.length > 0 ?
 Math.min(...validationDetails.distances.map(d => d.distance)) : null;
 const actualAllowedRadius = validationDetails?.distances?.length > 0 ?
 validationDetails.distances[0].allowedRadius :
 (allowedLocations?.length > 0 ? parseInt(allowedLocations[0].allowedRadius) : 300);

 if (nearestDistance && actualAllowedRadius) {
 const needToMove = Math.round(nearestDistance - actualAllowedRadius);

 // Format distance display - use km if > 1000m
 const formatDistance = (distance) => {
 if (distance >= 1000) {
 return `${(distance / 1000).toFixed(1)}km`;
 }
 return `${distance}m`;
 };

 // Lưu data cho modal thay vì hiển thị inline
 setLocationErrorData({
 nearestDistance,
 actualAllowedRadius,
 needToMove,
 formatDistance
 });
 setShowLocationModal(true);
 setStatus('not_checked_in');
 return;
 }
 }

 // Set detailed error message
 let errorMessage = apiError;

 if (statusCode === 403) {
 if (errorCode === 'DESKTOP_CHECKIN_FORBIDDEN' || errorCode === 'DESKTOP_CHECKOUT_FORBIDDEN') {
 errorMessage = 'Không được phép chấm công trên máy tính! Vui lòng sử dụng thiết bị di động.';
 } else {
 errorMessage = `Không có quyền thực hiện thao tác này! ${apiError}`;
 }
 } else if (statusCode === 404) {
 errorMessage = 'Không tìm thấy thông tin chấm công! Vui lòng liên hệ quản trị viên.';
 } else {
 errorMessage = `Lỗi hệ thống: ${apiError}`;
 }

 setError(errorMessage);

 // Re-fetch status
 try {
 const { data } = await API.get('/attendance/status/today');
 setStatus(data.status || 'not_checked_in');
 } catch (statusError) {
 setStatus('not_checked_in');
 }
 }
 };

 // Chuyển đến UserProfile tab Bảo mật & Chấm công
 const navigateToDeviceRegistration = () => {
 console.log('[NAVIGATION] Navigating to device registration...');
 setShowDeviceModal(false);

 // Delay navigation để tránh redirect
 setTimeout(() => {
 try {
 // Cách 1: React Router navigate (ưu tiên)
 console.log('[NAVIGATION] Using React Router navigate');
 navigate('/profile?tab=password&action=register-device', { replace: true });
 } catch (error) {
 console.error('[NAVIGATION] React Router navigate failed:', error);

 // Cách 2: window.history.pushState (tránh redirect)
 try {
 console.log('[NAVIGATION] Using window.history.pushState');
 window.history.pushState({}, '', '/profile?tab=password&action=register-device');
 // Trigger navigation event
 window.dispatchEvent(new PopStateEvent('popstate'));
 } catch (error2) {
 console.error('[NAVIGATION] window.history.pushState failed:', error2);

 // Cách 3: window.location.href (fallback)
 try {
 const baseUrl = window.location.origin;
 const targetUrl = `${baseUrl}/profile?tab=password&action=register-device`;
 console.log('[NAVIGATION] Using window.location.href:', targetUrl);
 window.location.href = targetUrl;
 } catch (error3) {
 console.error('[NAVIGATION] window.location.href failed:', error3);

 // Cách 4: window.location.assign (fallback)
 try {
 window.location.assign('/profile?tab=password&action=register-device');
 console.log('[NAVIGATION] Using window.location.assign');
 } catch (error4) {
 console.error('[NAVIGATION] All navigation methods failed:', error4);
 }
 }
 }
 }
 }, 100); // Delay 100ms
 };

 // Helper function để render modals
 const renderModals = () => (
 <>
 {/* Software Rating Modal */}
 <SoftwareRatingModal
 show={showRatingModal}
 onClose={() => setShowRatingModal(false)}
 onRatingSubmitted={handleRatingSubmitted}
 />

 {/* Location Error Modal */}
 <Modal show={showLocationModal} onHide={() => setShowLocationModal(false)} centered size="sm">
 <Modal.Header closeButton>
 <Modal.Title>
 <FaMapMarkerAlt className="me-2" />
 Lỗi vị trí
 </Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <div className="location-modal-content">
 <div className="location-icon">
 <FaMapMarkerAlt />
 </div>
 <h5 className="text-center mb-3">Không thể chấm công từ vị trí này!</h5>

 {locationErrorData && (
 <div className="location-stats">
 <div className="stat-item current">
 <div className="stat-label">Khoảng cách hiện tại</div>
 <div className="stat-value text-danger">
 {locationErrorData.formatDistance(Math.round(locationErrorData.nearestDistance))}
 </div>
 </div>

 <div className="stat-item allowed">
 <div className="stat-label">Phạm vi cho phép</div>
 <div className="stat-value text-success">
 {locationErrorData.formatDistance(locationErrorData.actualAllowedRadius)}
 </div>
 </div>

 <div className="action-required">
 <div className="action-icon"></div>
 <div className="action-text">
 Bạn cần di chuyển gần hơn <strong>{locationErrorData.formatDistance(locationErrorData.needToMove)}</strong> nữa!
 </div>
 </div>
 </div>
 )}

 <div className="help-text">
 Vui lòng di chuyển đến gần văn phòng công ty để có thể chấm công.
 </div>
 </div>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowLocationModal(false)}>
 Đóng
 </Button>
 <Button variant="primary" onClick={() => {
 setShowLocationModal(false);
 setError('');
 setStatus('not_checked_in');
 }}>
 <FaRedo className="me-2" />
 Thử lại
 </Button>
 </Modal.Footer>
 </Modal>

 {/* Device Registration Modal */}
 <Modal show={showDeviceModal} onHide={() => setShowDeviceModal(false)} centered>
 <Modal.Header closeButton>
 <Modal.Title>
 <FaMobile className="me-2" />
 Thiết bị chưa đăng ký
 </Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <div className="device-registration-content">
 <div className="device-icon-large">
 <FaMobile />
 </div>
 <h5 className="text-center mb-3">Thiết bị của bạn chưa được đăng ký!</h5>
 <p className="text-center text-muted mb-4">
 Để có thể chấm công, bạn cần đăng ký thiết bị này trước.
 Điều này giúp bảo mật và xác thực danh tính của bạn.
 </p>
 <div className="registration-steps">
 <div className="step-item">
 <div className="step-number">1</div>
 <div className="step-content">
 <strong>Đăng ký thiết bị</strong>
 <p>Nhấn nút bên dưới để chuyển đến trang đăng ký thiết bị</p>
 </div>
 </div>
 <div className="step-item">
 <div className="step-number">2</div>
 <div className="step-content">
 <strong>Xác thực danh tính</strong>
 <p>Làm theo hướng dẫn để xác thực thiết bị của bạn</p>
 </div>
 </div>
 <div className="step-item">
 <div className="step-number">3</div>
 <div className="step-content">
 <strong>Chấm công</strong>
 <p>Sau khi đăng ký thành công, bạn có thể chấm công bình thường</p>
 </div>
 </div>
 </div>
 </div>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowDeviceModal(false)}>
 Hủy bỏ
 </Button>
 <Button variant="primary" onClick={navigateToDeviceRegistration}>
 <FaCog className="me-2" />
 Đăng ký thiết bị
 </Button>
 </Modal.Footer>
 </Modal>
 </>
 );

 // Time logic
 const hour = now.getHours();
 const dayOfWeek = now.getDay();
 let isCheckinTime, isCheckoutTime;

 if (dayOfWeek === 6) { // Saturday
 isCheckinTime = hour >= 7 && hour < 11;
 isCheckoutTime = hour >= 11 && hour < 15;
 } else if (dayOfWeek >= 1 && dayOfWeek <= 5) { // Monday-Friday
 isCheckinTime = hour >= 5 && hour < 16;
 isCheckoutTime = hour >= 16 && hour < 22;
 } else { // Sunday
 isCheckinTime = false;
 isCheckoutTime = false;
 }

 if (status === 'loading') {
 return (
 <>
 {renderModals()}
 <div className="compact-attendance-bar loading">
 <div className="attendance-spinner"></div>
 <span>Đang tải...</span>
 </div>
 </>
 );
 }

 if (status === 'error') {
 return (
 <>
 {renderModals()}
 <div className="compact-attendance-bar error">
 <FaClock className="me-2" />
 <span>Lỗi chấm công</span>
 {error && (
 <div className="error-details mt-2">
 <small className="text-danger">{error}</small>
 <div className="mt-2">
 <button
 className="btn btn-sm btn-outline-primary"
 onClick={() => {
 setError('');
 setStatus('not_checked_in');
 }}
 >
 Thử lại
 </button>
 </div>
 </div>
 )}
 </div>
 </>
 );
 }

 // Desktop: chỉ hiện kết quả khi đã chấm công trên mobile/PWA
 if (isDesktop) {
 if (status === 'checked_out') {
 return (
 <>
 {renderModals()}
 <div className="compact-attendance-bar success">
 <FaCheckCircle className="me-2" />
 <span>Đã hoàn thành ngày làm việc</span>
 </div>
 </>
 );
 }
 if (status === 'checked_in') {
 let lateInfo = null;
 if (checkInTime) {
 const checkInHour = checkInTime.getHours();
 const checkInMinute = checkInTime.getMinutes();
 const checkInTotalMinutes = checkInHour * 60 + checkInMinute;
 const targetTime = 8 * 60;
 if (checkInTotalMinutes > targetTime) {
 const lateMinutes = checkInTotalMinutes - targetTime;
 const lateHours = Math.floor(lateMinutes / 60);
 const lateMinutesRemainder = lateMinutes % 60;
 if (lateHours > 0) {
 lateInfo = `Trễ ${lateHours} giờ ${lateMinutesRemainder > 0 ? lateMinutesRemainder + ' phút' : ''}`;
 } else {
 lateInfo = `Trễ ${lateMinutesRemainder} phút`;
 }
 }
 }
 return (
 <>
 {renderModals()}
 <div className="compact-attendance-bar status">
 <div className="status-info">
 <Badge bg="success" className="me-2">Đã xác thực</Badge>
 <span>Đã check-in lúc <strong>{checkInTime?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong></span>
 {lateInfo && (
 <span className="late-badge ms-2">{lateInfo}</span>
 )}
 </div>
 </div>
 </>
 );
 }
 // Desktop + chưa chấm công hoặc ngoài giờ → ẩn hoàn toàn
 return null;
 }

 // Mobile/PWA: Checked out - show success message
 if (status === 'checked_out') {
 return (
 <>
 {renderModals()}
 <div className="compact-attendance-bar success">
 <FaCheckCircle className="me-2" />
 <span>Đã hoàn thành ngày làm việc</span>
 </div>
 </>
 );
 }

 // Check-in phase
 console.log('Time check:', {
 status,
 isCheckinTime,
 isCheckoutTime,
 hour,
 dayOfWeek,
 hasRated
 });

 if (status === 'not_checked_in' && isCheckinTime) {
 return (
 <>
 {renderModals()}
 {/* Compact bar - hiển thị text đơn giản */}
 <div className="compact-attendance-bar checkin-simple">
 <FaMapMarkerAlt className="me-2" />
 <span>Chưa chấm công hôm nay</span>
 {!isDesktop && (
 <Button
 variant="link"
 size="sm"
 onClick={() => openModal('check-in')}
 className="reopen-btn"
 >
 Chấm công
 </Button>
 )}
 </div>

 {/* Hiển thị error nếu có */}
 {error && (
 <div className="compact-attendance-bar error mt-2">
 <FaClock className="me-2" />
 <span>Lỗi chấm công</span>
 <div className="error-details mt-2">
 <small className="text-danger">{error}</small>
 <div className="mt-2">
 <button
 className="btn btn-sm btn-outline-primary me-2"
 onClick={() => {
 setError('');
 openModal('check-in');
 }}
 >
 Thử lại
 </button>
 <button
 className="btn btn-sm btn-outline-secondary"
 onClick={() => window.location.reload()}
 >
 Tải lại
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Modal check-in - tự động hiện trên mobile */}
 <Modal
 show={showModal && modalAction === 'check-in'}
 onHide={closeModal}
 centered
 className="attendance-modal"
 backdrop="static"
 >
 <Modal.Header closeButton>
 <Modal.Title>
 <FaMapMarkerAlt style={{ fontSize: '1.2rem' }} />
 Chấm công vào
 </Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <div className="modal-content-text">
 <p>Bạn có muốn chấm công vào không?</p>
 <div className="time-display">
 <FaClock style={{ fontSize: '1.2rem', color: '#2f6bd8' }} />
 <small>Thời gian: {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
 </div>
 </div>
 {error && (
 <Alert variant="danger" className="mt-3 mb-0">
 {typeof error === 'string' && error.includes('Thiết bị chưa được đăng ký') ? (
 <div>
 <strong>Thiết bị chưa được đăng ký!</strong>
 <p className="mb-2">Vui lòng đăng ký thiết bị để có thể chấm công</p>
 <a
 href="/profile?tab=security&action=register-device"
 className="btn btn-sm btn-primary"
 >
 Đăng ký thiết bị ngay
 </a>
 </div>
 ) : (
 error
 )}
 </Alert>
 )}
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={closeModal}>
 Hủy
 </Button>
 <Button
 variant="success"
 onClick={() => performCheck('check-in')}
 disabled={status === 'loading'}
 >
 {status === 'loading' ? 'Đang xử lý...' : 'Chấm công vào'}
 </Button>
 </Modal.Footer>
 </Modal>
 </>
 );
 }

 // Check-out phase
 if (status === 'checked_in' && isCheckoutTime) {
 return (
 <>
 {renderModals()}
 {/* Compact bar - hiển thị text đơn giản */}
 <div className="compact-attendance-bar checkout-simple">
 <FaCheckCircle className="me-2" />
 <span>Đã vào: {checkInTime?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
 {!isDesktop && (
 <Button
 variant="link"
 size="sm"
 onClick={() => openModal('check-out')}
 className="reopen-btn"
 >
 Chấm công ra
 </Button>
 )}
 </div>

 {/* Modal check-out - tự động hiện trên mobile */}
 <Modal
 show={showModal && modalAction === 'check-out'}
 onHide={closeModal}
 centered
 className="attendance-modal"
 backdrop="static"
 >
 <Modal.Header closeButton>
 <Modal.Title>
 <FaCheckCircle style={{ fontSize: '1.2rem' }} />
 Chấm công ra
 </Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <div className="modal-content-text">
 <p>Bạn có muốn chấm công ra không?</p>
 <div className="time-info">
 <div className="time-row">
 <FaClock style={{ fontSize: '1rem', color: '#2f6bd8' }} />
 <small>Giờ vào: {checkInTime?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
 </div>
 <div className="time-row">
 <FaClock style={{ fontSize: '1rem', color: '#2f6bd8' }} />
 <small>Giờ ra: {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
 </div>
 </div>
 </div>
 {error && (
 <Alert variant="danger" className="mt-3 mb-0">
 {typeof error === 'string' && error.includes('Thiết bị chưa được đăng ký') ? (
 <div>
 <strong>Thiết bị chưa được đăng ký!</strong>
 <p className="mb-2">Vui lòng đăng ký thiết bị để có thể chấm công</p>
 <a
 href="/profile?tab=security&action=register-device"
 className="btn btn-sm btn-primary"
 >
 Đăng ký thiết bị ngay
 </a>
 </div>
 ) : (
 error
 )}
 </Alert>
 )}
 </Modal.Body>
 <Modal.Footer>
 <Button variant="secondary" onClick={closeModal}>
 Hủy
 </Button>
 <Button
 variant="warning"
 onClick={() => performCheck('check-out')}
 disabled={status === 'loading'}
 >
 {status === 'loading' ? 'Đang xử lý...' : 'Chấm công ra'}
 </Button>
 </Modal.Footer>
 </Modal>
 </>
 );
 }

 // Mid-day phase - compact status
 if (status === 'checked_in') {
 // Tính toán số phút trễ so với 8h sáng
 let lateInfo = null;
 if (checkInTime) {
 const checkInHour = checkInTime.getHours();
 const checkInMinute = checkInTime.getMinutes();

 // Tính tổng số phút từ 0h
 const checkInTotalMinutes = checkInHour * 60 + checkInMinute;
 const targetTime = 8 * 60; // 8h sáng = 480 phút

 if (checkInTotalMinutes > targetTime) {
 const lateMinutes = checkInTotalMinutes - targetTime;
 const lateHours = Math.floor(lateMinutes / 60);
 const lateMinutesRemainder = lateMinutes % 60;

 if (lateHours > 0) {
 lateInfo = `Trễ ${lateHours} giờ ${lateMinutesRemainder > 0 ? lateMinutesRemainder + ' phút' : ''}`;
 } else {
 lateInfo = `Trễ ${lateMinutesRemainder} phút`;
 }
 }
 }

 return (
 <>
 {renderModals()}
 <div className="compact-attendance-bar status">
 <div className="status-info">
 <Badge bg="success" className="me-2">Đã xác thực</Badge>
 <span>Đã check-in lúc <strong>{checkInTime?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong></span>
 {lateInfo && (
 <span className="late-badge ms-2">{lateInfo}</span>
 )}
 </div>
 </div>
 </>
 );
 }

 // Outside working hours
 return (
 <>
 {renderModals()}
 <div className="compact-attendance-bar">
 <FaClock className="me-2" />
 <span>Ngoài giờ làm việc</span>
 </div>
 </>
 );
};

export default CompactAttendanceBar;
